<template>
  <div
    v-if="isVertical"
    :class="[
      'bg-white/95 backdrop-blur-md rounded-2xl p-4 border border-gray-100 shadow-sm',
      className
    ]"
  >
    <h3
      class="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 px-2"
    >
      Menu Categories
    </h3>
    <div class="flex flex-col space-y-1">
      <button
        v-for="cat in categoriesList"
        :key="cat.id"
        @click="scrollToCategory(cat.id)"
        :class="[
          'text-left w-full px-4 py-3 rounded-xl text-sm font-medium transition-all flex items-center justify-between group',
          isCategoryActive(cat.id)
            ? 'bg-primary-50 text-primary-700 font-bold shadow-sm'
            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
        ]"
      >
        <span>{{ cat.name }}</span>
        <ChevronRight
          v-if="isCategoryActive(cat.id)"
          :size="16"
          class="text-primary-600"
        />
      </button>
    </div>
  </div>

  <!-- Horizontal (Mobile) View -->
  <div v-else :class="['border-b border-gray-200 shadow-sm', className]">
    <div class="bg-white/95 backdrop-blur-md">
      <div class="container mx-auto px-4">
        <div class="flex items-center gap-2">
          <button
            type="button"
            aria-label="Browse all categories"
            @click="showCategoryMenu = true"
            class="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-full border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-all"
          >
            <List :size="18" />
          </button>
          <div
            ref="categoryStrip"
            class="flex-1 flex overflow-x-auto no-scrollbar py-3 gap-3"
          >
            <button
              v-for="cat in categoriesList"
              :key="cat.id"
              ref="catBtn"
              @click="scrollToCategory(cat.id)"
              :class="[
                'whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-bold transition-all border flex-shrink-0',
                isCategoryActive(cat.id)
                  ? 'bg-primary-600 text-white border-primary-600 shadow-md shadow-primary-600/20'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300'
              ]"
            >
              {{ cat.name }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Full category list overlay -->
    <div
      v-if="showCategoryMenu"
      class="fixed inset-0 z-50"
      @click.self="showCategoryMenu = false"
    >
      <div
        class="absolute inset-0 bg-black/50"
        @click="showCategoryMenu = false"
      ></div>
      <div
        class="absolute left-0 right-0 bottom-0 bg-white rounded-t-2xl shadow-2xl flex flex-col"
        style="top: 96px"
      >
        <div
          class="flex items-center justify-between px-5 pt-4 pb-3 border-b border-gray-100"
        >
          <h3 class="text-lg font-bold text-gray-900">Browse Categories</h3>
          <button
            type="button"
            aria-label="Close menu"
            @click="showCategoryMenu = false"
            class="flex items-center justify-center w-9 h-9 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-all"
          >
            <X :size="20" />
          </button>
        </div>
        <div class="flex-1 overflow-y-auto px-4 py-3">
          <button
            v-for="cat in categoriesList"
            :key="`menu-${cat.id}`"
            @click="selectCategoryFromMenu(cat.id)"
            :class="[
              'text-left w-full px-4 py-4 mb-2 rounded-xl text-base transition-all flex items-center justify-between gap-3',
              isCategoryActive(cat.id)
                ? 'bg-primary-50 text-primary-700 font-bold ring-1 ring-primary-200'
                : 'bg-gray-50 text-gray-700 font-medium hover:bg-gray-100 active:bg-gray-100'
            ]"
          >
            <span class="truncate">{{ cat.name }}</span>
            <ChevronRight
              :size="20"
              :class="
                isCategoryActive(cat.id) ? 'text-primary-600' : 'text-gray-300'
              "
            />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { List } from "lucide-vue";

export default {
  name: "CategoryNav",
  components: {
    List
  },
  props: {
    isVertical: {
      type: Boolean,
      default: false
    },
    className: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      activeId: null,
      observer: null,
      showCategoryMenu: false
    };
  },
  computed: {
    ...mapGetters(["categories"]),
    categoriesList() {
      // Use categories from backend only
      return this.categories || [];
    }
  },
  watch: {
    categoriesList: {
      immediate: true,
      handler(newCategories) {
        if (newCategories && newCategories.length > 0) {
          // Check for category in URL hash
          const categoryFromUrl = this.getCategoryFromUrl();
          if (categoryFromUrl) {
            this.activeId = categoryFromUrl;
            // Scroll to category after a short delay to ensure DOM is ready
            this.$nextTick(() => {
              setTimeout(() => {
                const categoryId = categoryFromUrl.replace("cat_", "");
                this.scrollToCategory(categoryId);
              }, 300);
            });
          } else {
            // Set initial active ID with correct format
            if (!this.activeId) {
              const firstCategory = newCategories[0];
              this.activeId =
                typeof firstCategory.id === "string" &&
                firstCategory.id.startsWith("cat_")
                  ? firstCategory.id
                  : `cat_${firstCategory.id}`;
            }
          }
          // Setup observer when categories are loaded
          this.$nextTick(() => {
            this.setupObserver();
          });
        }
      }
    },
    $route() {
      // Watch for route changes to update active category
      this.handleHashChange();
    },
    activeId(newId) {
      // On mobile horizontal strip: scroll the active category button into view
      if (newId && !this.isVertical) {
        this.$nextTick(() => this.scrollActiveCategoryIntoView());
      }
    }
  },
  mounted() {
    this.setupObserver();
    // Listen for hash changes
    window.addEventListener("hashchange", this.handleHashChange);
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    window.removeEventListener("hashchange", this.handleHashChange);
  },
  methods: {
    getCategoryFromUrl() {
      // Check for category in hash (format: #cat_{categoryId})
      const hash = window.location.hash;
      if (hash && hash.startsWith("#cat_")) {
        return hash.substring(1); // Remove the # to get 'cat_{categoryId}'
      }

      return null;
    },
    handleHashChange() {
      // Handle hash changes to update active category
      const categoryFromUrl = this.getCategoryFromUrl();
      if (categoryFromUrl) {
        this.activeId = categoryFromUrl;
        this.$nextTick(() => {
          setTimeout(() => {
            this.scrollToCategory(categoryFromUrl.replace("cat_", ""));
          }, 100);
        });
      }
    },
    isCategoryActive(categoryId) {
      // Compare category ID with activeId, handling both formats
      const catIdFormatted =
        typeof categoryId === "string" && categoryId.startsWith("cat_")
          ? categoryId
          : `cat_${categoryId}`;
      return this.activeId === catIdFormatted || this.activeId === categoryId;
    },
    setupObserver() {
      if (this.observer) {
        this.observer.disconnect();
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // Extract category ID from element ID (format: cat_123)
              const elementId = entry.target.id;
              this.activeId = elementId;

              // Update URL hash when category becomes active during scroll
              const categoryId = elementId.replace("cat_", "");
              const currentUrl =
                window.location.pathname + window.location.search;
              const newHash = `#cat_${categoryId}`;

              // Only update if hash is different to avoid unnecessary history entries
              if (window.location.hash !== newHash) {
                window.history.replaceState(
                  null,
                  "",
                  `${currentUrl}${newHash}`
                );
              }
            }
          });
        },
        { rootMargin: "-20% 0px -60% 0px", threshold: 0 }
      );

      // Observe category sections (format: cat_${category.id})
      this.categoriesList.forEach((cat) => {
        // Backend categories have numeric IDs, fallback might have string IDs
        const categoryId =
          typeof cat.id === "string" && cat.id.startsWith("cat_")
            ? cat.id
            : `cat_${cat.id}`;
        const el = document.getElementById(categoryId);
        if (el) {
          this.observer.observe(el);
        }
      });
    },
    scrollActiveCategoryIntoView() {
      const activeId = this.activeId;
      if (!activeId) return;
      const index = this.categoriesList.findIndex((cat) => {
        const formatted =
          typeof cat.id === "string" && cat.id.startsWith("cat_")
            ? cat.id
            : `cat_${cat.id}`;
        return activeId === formatted || activeId === String(cat.id);
      });
      if (index === -1) return;
      const strip = this.$refs.categoryStrip;
      const refs = this.$refs.catBtn;
      const node = Array.isArray(refs) ? refs[index] : refs;
      if (!strip || !node) return;
      // Scroll the strip horizontally only (centering the active button).
      // Using scrollIntoView here also scrolls the page vertically, which
      // fought the user's downward scroll, so adjust scrollLeft directly.
      const stripRect = strip.getBoundingClientRect();
      const nodeRect = node.getBoundingClientRect();
      const delta =
        nodeRect.left -
        stripRect.left -
        (strip.clientWidth / 2 - nodeRect.width / 2);
      strip.scrollTo({ left: strip.scrollLeft + delta, behavior: "smooth" });
    },
    selectCategoryFromMenu(categoryId) {
      this.showCategoryMenu = false;
      this.scrollToCategory(categoryId);
    },
    scrollToCategory(categoryId) {
      // Ensure format is cat_${id} for backend categories
      // categoryId can be either a number (from backend) or a string (from fallback)
      let categoryElementId;
      if (typeof categoryId === "string") {
        // If it's already formatted or from fallback
        categoryElementId = categoryId.startsWith("cat_")
          ? categoryId
          : `cat_${categoryId}`;
      } else {
        // If it's a number from backend
        categoryElementId = `cat_${categoryId}`;
      }

      const el = document.getElementById(categoryElementId);
      if (el) {
        const offset = 160;
        const y = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: y, behavior: "smooth" });
        // Update active ID immediately for better UX
        this.activeId = categoryElementId;

        // Update URL with hash (format: #cat_{categoryId}) without page reload
        const categoryIdForHash =
          typeof categoryId === "string" && categoryId.startsWith("cat_")
            ? categoryId
            : `cat_${categoryId}`;
        // Use replaceState to update hash without triggering navigation
        const currentUrl = window.location.pathname + window.location.search;
        window.history.replaceState(
          null,
          "",
          `${currentUrl}#${categoryIdForHash}`
        );
      }
    }
  }
};
</script>
